import React from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import { type ISlot } from '../../../models/DistributionPlan'
import { format } from 'date-fns'
import { ReleaseMode } from '../../../helpers/parseReleaseTimestamp'
import { Calendar, CalendarCheck, GraduationCap, PanelTop } from 'lucide-react'
import { formatTermDateRangeByUserTimeZone } from '../../../helpers/formatDateByUserTimeZone'
import { useOptionalWarningHighlight } from '../../../components/CustomTable/WarningHighlighterContext'
import { detailLinkStyle, WarningStyles } from './styles'
import { DetailHighlightType } from '../../../components/CustomTable/CellTypes/constant'
import ROUTES from '../../../consts/paths'
import { useAllowedPath } from '../../../hooks/useAllowedPath'

interface DistributionPlanDetailsProps {
  slot: ISlot
}
interface DetailItem {
  title: string
  key: DetailHighlightType
  icon: JSX.Element
  value?: string
  linkTo?: string
}

interface DetailValueProps {
  value?: string
  linkTo?: string
}

const DetailValue: React.FC<DetailValueProps> = ({ value, linkTo }) => {
  const canLink = useAllowedPath(linkTo ?? '')

  if (linkTo && canLink && value) {
    return (
      <Link
        to={linkTo}
        target="_blank"
        rel="noopener noreferrer"
        style={detailLinkStyle}
        onMouseEnter={(event) => { event.currentTarget.style.textDecoration = 'underline' }}
        onMouseLeave={(event) => { event.currentTarget.style.textDecoration = 'none' }}
      >
        {value}
      </Link>
    )
  }

  return <>{value}</>
}

const DistributionPlanDetails: React.FC<DistributionPlanDetailsProps> = ({ slot }) => {
  const releaseTimestamp = slot.releaseTimestamp
  const startTimestamp = slot.slotStartTimestamp
  const endTimestamp = slot.slotEndTimestamp
  const rollingRelease = slot.isRollingResultsEnabled
  const formattedResultsDate = releaseTimestamp === ReleaseMode.Rolling ? releaseTimestamp : format(new Date(releaseTimestamp), 'MMM d, yyyy')

  const details: DetailItem[] = [
    {
      title: 'Term',
      key: DetailHighlightType.TERM,
      icon: <GraduationCap size={18} />,
      value: slot?.term?.termName,
      linkTo: slot?.term?.termId ? `${ROUTES.TERMS_DETAILS}/${slot.term.termId}` : undefined
    },
    {
      title: 'Survey Template',
      key: DetailHighlightType.SURVEY_TEMPLATE,
      icon: <PanelTop size={18} />,
      value: slot?.surveyTemplate?.title,
      linkTo: slot?.surveyTemplate?.templateId ? `${ROUTES.SURVEY_LIST}/${slot.surveyTemplate.templateId}` : undefined
    },
    { title: 'Survey Project Date Range', key: DetailHighlightType.DATE_RANGE, icon: <Calendar size={18} />, value: formatTermDateRangeByUserTimeZone(startTimestamp, endTimestamp) },
    rollingRelease
      ? { title: 'Rolling Results', key: DetailHighlightType.ROLLING_RESULTS, icon: <CalendarCheck size={18}/>, value: 'Rolling Results' }
      : { title: 'Survey Project Results Release Date', key: DetailHighlightType.RESULTS_RELEASE_DATE, icon: <CalendarCheck size={18} />, value: formattedResultsDate }
  ]

  // Registers cells for highlighting if warning highlight context is available
  const warningHighlight = useOptionalWarningHighlight()
  const registerCellRef = warningHighlight?.registerCellRef
  const highlightedCells = warningHighlight?.highlightedCells ?? {}

  return (
    <Box mt={1}>
      <Typography tabIndex={0} variant="h3">
        <Box display="flex" alignItems="center" height="100%" flexWrap="wrap" gap={3}>
          {details?.map(({ title, icon, value, key, linkTo }) => {
            const highlightKey = `${DetailHighlightType.OUTSIDE_TABLE}:${key}`
            const isHighlighted = highlightedCells[highlightKey]

            return (
              <Tooltip key={title} title={title} placement='bottom-start'>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.75}
                  height="100%"
                  flexWrap="nowrap"
                  ref={(el: HTMLDivElement | null) => {
                    if (registerCellRef) {
                      if (el) registerCellRef(DetailHighlightType.OUTSIDE_TABLE, key, el)
                      else registerCellRef(DetailHighlightType.OUTSIDE_TABLE, key, null)
                    }
                  }}
                  style={isHighlighted ? WarningStyles : undefined}
                >
                    {icon}<DetailValue value={value} linkTo={linkTo} />
                </Box>
              </Tooltip>
            )
          })}
        </Box>
      </Typography>
    </Box>
  )
}
export default DistributionPlanDetails
